import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { getCookie } from "../../utils/cookieHandler";
import { config } from "../../utils/config";
import { PropagateLoader } from "react-spinners";

import "./DashboardPage.css";

// Dashboard embed URLs
const DASHBOARD_EMBEDS = {
  UAE: "https://lookerstudio.google.com/embed/reporting/91724435-d339-43c3-bf7f-68f0ef4959cb/page/tWnMF",
  LB: "https://lookerstudio.google.com/embed/reporting/45f46b62-8687-4f3c-9d5b-f95700d6e179/page/p_6o44qmvttd",
  ADMIN:
    "https://lookerstudio.google.com/embed/reporting/026df340-a15e-4fa6-9318-b8122bcd0f19/page/tWnMF",
  EXECUTIVE:
    "https://lookerstudio.google.com/embed/reporting/86009bd6-7ba0-49dc-a8b3-87e2a9a1eded/page/tWnMF",
};

// Role constants
const ADMIN_ROLE = process.env.REACT_APP_ADMIN_ROLE;
const MANAGER_ROLE = process.env.REACT_APP_MANAGER_ROLE;
const FINANCE_ROLE = process.env.REACT_APP_FINANCE_ROLE;
const CAPTAIN_ROLE = process.env.REACT_APP_CAPTAIN_ID;
const OPERATOR_ROLE = process.env.REACT_APP_OPERATOR_ROLE;
const CLIENT_ROLE = process.env.REACT_APP_CLIENT_ROLE;
const PARTNER_ROLE = process.env.REACT_APP_PARTNER_ROLE;

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [currentView, setCurrentView] = useState("dashboard"); // 'dashboard' or 'executive'
  const [error, setError] = useState(null);
  const iframeContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const token = getCookie("_AT");

      if (!token) {
        setError("No authentication token found");
        setLoading(false);
        return;
      }

      // Fetch user data including branches from /me endpoint
      const userResponse = await axios.get(`${config.apiUrl}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUserData(userResponse.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const getDashboardUrl = () => {
    if (!userData) return DASHBOARD_EMBEDS.ADMIN;

    const userRole = userData.role?.id;
    const isAdmin = userRole === ADMIN_ROLE;
    const isManager = userRole === MANAGER_ROLE;
    const isFinance = userRole === FINANCE_ROLE;
    const isClient = userRole === CLIENT_ROLE;
    
    // Admin users always see Admin Dashboard
    if (isAdmin) {
      return DASHBOARD_EMBEDS.ADMIN;
    }

    if (isClient) {
      // Return null or a specific client URL if available
      return null;
    }

    if (isClient) {
      // Return null or a specific client URL if available
      return null;
    }

    // Manager and Finance users see dashboard based on their branches
    if (
      (isManager || isFinance) &&
      userData.branches &&
      userData.branches.length > 0
    ) {
      const branchCodes = userData.branches.map(
        (userBranch) => userBranch.branch_id?.code
      );

      // Check if user has both AE (UAE) and LB branches
      const hasAE = branchCodes.includes("AE");
      const hasLB = branchCodes.includes("LB");

      if (hasAE && hasLB) {
        // User has both branches - show Admin Dashboard
        return DASHBOARD_EMBEDS.ADMIN;
      } else if (hasAE) {
        // User has only AE branch (UAE)
        return DASHBOARD_EMBEDS.UAE;
      } else if (hasLB) {
        // User has only LB branch
        return DASHBOARD_EMBEDS.LB;
      }
    }

    // Default fallback
    return DASHBOARD_EMBEDS.ADMIN;
  };

  const getCurrentEmbedUrl = () => {
    if (currentView === "executive") {
      return DASHBOARD_EMBEDS.EXECUTIVE;
    }
    return getDashboardUrl();
  };

  const getDashboardTitle = () => {
    if (currentView === "executive") {
      return "Executive Summary";
    }

    if (!userData) return "Dashboard";

    const userRole = userData.role?.id;
    const isAdmin = userRole === ADMIN_ROLE;
    const isManager = userRole === MANAGER_ROLE;
    const isFinance = userRole === FINANCE_ROLE;
    const isClient = userRole === CLIENT_ROLE;

    if (isAdmin) {
      return "Admin Dashboard";
    }

    if (isClient) {
      return "Client Dashboard";
    }

    if (
      (isManager || isFinance) &&
      userData.branches &&
      userData.branches.length > 0
    ) {
      const branchCodes = userData.branches.map(
        (userBranch) => userBranch.branch_id?.code
      );

      const hasAE = branchCodes.includes("AE");
      const hasLB = branchCodes.includes("LB");

      if (hasAE && hasLB) {
        return "All Branches Dashboard";
      } else if (hasAE) {
        return "UAE Dashboard";
      } else if (hasLB) {
        return "LB Dashboard";
      }
    }

    return "Dashboard";
  };

  const canAccessExecutiveSummary = () => {
    return userData?.role?.id === ADMIN_ROLE;
  };

  const isCaptain = () => {
    return userData?.role?.id === CAPTAIN_ROLE;
  };

  const isOperator = () => {
    return userData?.role?.id === OPERATOR_ROLE;
  };
  // Fullscreen handler
  const handleFullscreen = () => {
    const elem = iframeContainerRef.current;
    if (!elem) return;
    if (!document.fullscreenElement) {
      elem.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen change to update state
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  if (loading) {
    return (
      <div className="flex h-96 justify-center items-center">
        <PropagateLoader color="#0167ff" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 justify-center items-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={fetchUserData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }



  const isClient = () => {
    return userData?.role?.id === CLIENT_ROLE;
  };

  const isPartner = () => {
    return userData?.role?.id === PARTNER_ROLE;
  };

  // Access control
  if (isCaptain() || isOperator() || isClient() || isPartner()) {
    return (
      <div>
        <Helmet>
          <title>No Access | Glide</title>
          <meta name="description" content="Access denied for this role." />
        </Helmet>

        <div className="flex h-96 justify-center items-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">No Access</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to view the dashboard.
            </p>
            <button
              onClick={() => (window.location.href = "/reservations")}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Go To Reservations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{getDashboardTitle()} | Glide</title>
        <meta
          name="description"
          content="Car2Go Dashboard - Overview of your rental business."
        />
      </Helmet>

      <div className="dashboard-container">
        {/* Admin Controls */}
        {canAccessExecutiveSummary() && (
          <div className="mb-6 flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                onClick={() => setCurrentView("dashboard")}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  currentView === "dashboard"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-blue-100"
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setCurrentView("executive")}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  currentView === "executive"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-blue-100"
                }`}
              >
                Executive Summary
              </button>
            </div>
            <div className="text-sm text-gray-600">
              Current view:{" "}
              <span className="font-medium">{getDashboardTitle()}</span>
            </div>
          </div>
        )}

        {/* Dashboard Embed */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
          <div className="dashboard-iframe-container" ref={iframeContainerRef}>
            {/* Fullscreen Button */}
            <button
              type="button"
              onClick={handleFullscreen}
              aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              className="absolute top-3 right-3 z-10 bg-blue-800 bg-opacity-80 text-white rounded-full p-2 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
            >
              {isFullscreen ? (
                // Exit fullscreen icon (minimize)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 15v5h5m6-5h5v5m0-16v5h-5m-6-5H4v5"
                  />
                </svg>
              ) : (
                // Enter fullscreen icon (maximize)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-16h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3"
                  />
                </svg>
              )}
            </button>
            <iframe
              src={getCurrentEmbedUrl()}
              frameBorder="0"
              allowFullScreen
              sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
              title={getDashboardTitle()}
              key={getCurrentEmbedUrl()}
              className="w-full h-full"
            />
          </div>
        </div>

        {/* Debug Info (only in development)
        {process.env.NODE_ENV === 'development' && userData && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
            <p><strong>Debug Info:</strong></p>
            <p>User Role: {userData.role?.name} ({userData.role?.id})</p>
            <p>User Branches: {userData.branches?.map(b => `${b.branch_id?.Name} (${b.branch_id?.code})`).join(', ') || 'None'}</p>
            <p>Dashboard URL: {getCurrentEmbedUrl()}</p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default DashboardPage;
